import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { Bridge, BridgeType } from "../../../../types/bridge";
import {
  BRIDGE_TYPES,
  calculateBridgeCost,
  calculateBridgeWeight,
} from "../../../../constants/bridges";
import { setBridge } from "../../../../store/features/shipBuilderSlice";
import { selectIsBreakaway } from "../../../../store/selectors/breakawayHullSelectors";
import { BreakawayBridgeConfig } from "./BreakawayBridgeConfig";


/**
 * Component for selecting and configuring ship bridges
 * Handles bridge type, size, and cost calculations according to Traveller ruleset
 *
 * @component
 * @example
 * ```tsx
 * <BridgeSelector />
 * ```
 */
export const BridgeSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipSize = useAppSelector(selectors.selectShipSize);
  const techLevel = useAppSelector(selectors.selectTechLevel);
  const currentBridge = useAppSelector(selectors.selectBridge);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const sizeOverrideActive = useAppSelector(selectors.selectSizeOverrideActive);
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);


  // Check if breakaway hulls are configured
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const getBreakawayInfo = () => {
    if (!breakawayHull?.sections) return "";
    const sections = breakawayHull.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name}: ${s.size} tons`).join(", ")})`;
  };



  const handleBridgeSelect = (bridgeType: BridgeType) => {
    const bridgeConfig = BRIDGE_TYPES[bridgeType];

    // Calculate actual bridge size and cost using Traveller ruleset
    const bridgeSize = calculateBridgeWeight(bridgeType, shipSize);
    const bridgeCost = calculateBridgeCost(bridgeType, shipSize);

    // Calculate maintenance cost as 1% of bridge cost
    const maintenanceCost = Math.floor(bridgeCost * 0.01);

    const bridge: Bridge = {
      type: bridgeType,
      cost: bridgeCost,
      weight: bridgeSize,
      techLevel: bridgeConfig.techLevel,
      special: bridgeConfig.special,
      maintenanceCost: maintenanceCost,
      power: bridgeConfig.power,
      sizeRange: bridgeConfig.sizeRange,
      description: bridgeConfig.description,
      id: bridgeConfig.id,
    };

    dispatch(setBridge(bridge));
  };

  const getBridgeAvailability = (bridgeType: BridgeType) => {
    const config = BRIDGE_TYPES[bridgeType];

    // Check tech level requirement
    if (config.techLevel > techLevel) {
      return {
        available: false,
        reason: `Requires Tech Level ${config.techLevel}`,
      };
    }

    // Check size requirements
    if (!sizeOverrideActive && shipSize < config.sizeRange.min) {
      return {
        available: false,
        reason: `Minimum ship size: ${config.sizeRange.min} tons`,
      };
    }

    if (!sizeOverrideActive && shipSize > config.sizeRange.max) {
      return {
        available: false,
        reason: `Maximum ship size: ${config.sizeRange.max} tons`,
      };
    }

    return { available: true, reason: "" };
  };


  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Bridge Configuration
        </h2>

      </div>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Breakaway Hulls Detected
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              {getBreakawayInfo()}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> Each breakaway section can have its own bridge configuration. 
              When sections are together, the main bridge provides overall ship control.
            </p>
          </div>
        </div>
      )}

      {/* Breakaway Bridge Configuration - Inline */}
      {hasBreakawayHulls && breakawayHull?.sections && breakawayHull.sections.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Breakaway Section Bridge Configuration</h3>
          <BreakawayBridgeConfig
            isOpen={true}
            onClose={() => {}} // No-op since we're showing inline
            inline={true}
          />
        </div>
      )}

      {/* Standard Bridge Selection - Hidden when breakaway hulls are enabled */}
      {!hasBreakawayHulls && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Bridge Selection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(BRIDGE_TYPES).map(([type, config]) => {
            const bridgeType = type as BridgeType;
            const availability = getBridgeAvailability(bridgeType);
            const isSelected = currentBridge?.type === bridgeType;

            // Map bridge type to short display name
            const bridgeTypeNames: Record<string, string> = {
              STANDARD: "Standard Bridge",
              SMALLER: "Smaller Bridge",
              COMMAND: "Command Bridge",
              COCKPIT: "Single-seat Cockpit",
              DUAL_COCKPIT: "Dual-seat Cockpit",
            };
            const displayName = bridgeTypeNames[bridgeType] || bridgeType;

            const bridgeConfig = BRIDGE_TYPES[bridgeType];
            const isAvailable = (tlOverrideActive || bridgeConfig.techLevel <= techLevel) && 
                              (sizeOverrideActive || availability.available);

            return (
              <button
                key={bridgeType}
                onClick={() => handleBridgeSelect(bridgeType)}
                disabled={!isAvailable}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  isSelected
                    ? "border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-900/30"
                    : isAvailable
                      ? "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      : "opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"
                }`}
              >
                <h3 className="font-semibold text-lg mb-1 text-gray-900 dark:text-gray-100">{displayName}</h3>
                {!isAvailable && !tlOverrideActive && !sizeOverrideActive && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    {availability.reason || `Requires Tech Level ${bridgeConfig.techLevel}`}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Breakaway Hulls Message - Show when breakaway hulls are enabled but no sections configured */}
      {hasBreakawayHulls && !breakawayHull?.sections?.length && (
        <div className="mb-6 p-4 border-2 border-yellow-300 rounded-lg bg-yellow-50">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            Breakaway Hulls Detected
          </h3>
          <p className="text-sm text-yellow-700">
            Breakaway hulls are enabled but sections are not yet configured. 
            Please configure breakaway sections in the Hull step before configuring bridges.
          </p>
        </div>
      )}
    </div>
  );
};
